/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module AviFormsModule */

import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';
import * as l10n from './string-group-or-custom-value.l10n';
import './string-group-or-custom-value.component.less';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

enum StringType {
    STRING_GROUP = 'STRING_GROUP',
    CUSTOM_VALUE = 'CUSTOM_VALUE',
}

/**
 * Config object containing String Group ref, list of match strings, and the stringType.
 */
interface IStringGroupRefsOrMatchStringsConfig {
    stringGroupRef: string;
    matchStrings: string[];
    stringType: StringType;
}

/**
 * Return an empty config object.
 */
const getDefaultConfig = (): IStringGroupRefsOrMatchStringsConfig => {
    return {
        stringGroupRef: undefined,
        matchStrings: [],
        stringType: StringType.STRING_GROUP,
    };
};

/**
 * @description
 *     Component for entering string group refs or custom values. Custom values use a repeated string input, while
 *     String Group selection uses 1 group per line to allow for editing the group.
 * @author alextsg
 */
@Component({
    selector: 'string-group-or-custom-value',
    templateUrl: './string-group-or-custom-value.component.html',
})
export class StringGroupOrCustomValueComponent implements OnInit, OnDestroy {
    /**
     * List of custom value strings.
     */
    @Input()
    public customValues: string[] | undefined;

    /**
     * List of string group refs.
     */
    @Input()
    public stringGroupRefs: string[] | undefined;

    /**
     * Flag to track if the String Group field is required.
     * For Client Insights inside the VS Modal, all the fields of this match are optional.
     */
    @Input()
    public areStringGroupOrCustomFieldsRequired = true;

    /**
     * Flag to track if duplicate custom values are allowed.
     */
    @Input()
    public areDuplicateCustomValuesAllowed = false;

    /**
     * EventEmitter for custom value strings.
     */
    @Output()
    public customValuesChange = new EventEmitter<string[]>();

    /**
     * EventEmitter for string group refs.
     */
    @Output()
    public stringGroupRefsChange = new EventEmitter<string[]>();

    /**
     * This component can be used repeatedly in a match, so to ensure the ngModel has a unique name
     * the index can be passed in as a binding.
     */
    @Input()
    public id: string | number = 0;

    /**
     * Disable any modifications to the fields.
     */
    @Input()
    public disabled = false;

    /**
     * If true, only custom values can be configured.
     */
    @Input()
    public disallowStringGroups = false;

    @Input()
    public noMarginTop = false;

    @Input()
    public label: string;

    /**
     * Flag to hide or show the add item button.
     */
    @Input()
    public showAddItemButton = true;

    public headerLabel: string;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    public stringGroupRefOrCustomValuesConfigs: IStringGroupRefsOrMatchStringsConfig[] = [];

    /**
     * stringGroupCollection instance for selecting a String Group.
     */
    public readonly stringGroupCollection: any;

    /**
     * Custom StringType enum. Used for selecting between the radio options for String Group or
     * custom value.
     */
    public StringType = StringType;

    constructor(
        private readonly l10nService: L10nService,
        @Inject('StringGroupCollection')
        StringGroupCollection: any,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.stringGroupCollection = new StringGroupCollection();
    }

    /** @override */
    public ngOnInit(): void {
        this.setStringGroupRefOrCustomValuesConfigs();

        this.headerLabel = this.label || this.l10nService.getMessage(globalL10nKeys.stringLabel);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.stringGroupCollection.destroy();
    }

    public handleStringTypeChange(index: number): void {
        const config = this.stringGroupRefOrCustomValuesConfigs[index];

        config.matchStrings = [];
        config.stringGroupRef = undefined;
        this.handleCustomValuesChange();
        this.handleStringGroupRefsChange();
    }

    public handleCustomValuesChange(): void {
        const customValues = this.stringGroupRefOrCustomValuesConfigs.reduce((acc, config) => {
            const { matchStrings } = config;

            return matchStrings.length ? acc.concat(...config.matchStrings) : acc;
        }, []);

        this.customValuesChange.emit(customValues);
    }

    /**
     * Handle change in selected string groups.
     */
    public handleStringGroupRefsChange(): void {
        const stringGroupRefs = this.stringGroupRefOrCustomValuesConfigs.reduce((acc, config) => {
            const { stringGroupRef } = config;

            return stringGroupRef ? acc.concat(config.stringGroupRef) : acc;
        }, []);

        this.stringGroupRefsChange.emit(stringGroupRefs);
    }

    /**
     * Add a new item to the list of configs.
     */
    public addItem(): void {
        this.stringGroupRefOrCustomValuesConfigs.push(getDefaultConfig());
    }

    /**
     * Remove an item from the list of configs.
     */
    public removeItem(index: number): void {
        this.stringGroupRefOrCustomValuesConfigs = [
            ...this.stringGroupRefOrCustomValuesConfigs.slice(0, index),
            ...this.stringGroupRefOrCustomValuesConfigs.slice(index + 1),
        ];

        this.handleCustomValuesChange();
        this.handleStringGroupRefsChange();
    }

    public trackByIndex(index: number): number {
        return index;
    }

    /**
     * Set the list of config objects based on the existing configuration.
     */
    private setStringGroupRefOrCustomValuesConfigs(): void {
        if (!this.disallowStringGroups && this.stringGroupRefs?.length) {
            this.stringGroupRefs.forEach(stringGroupRef => {
                this.stringGroupRefOrCustomValuesConfigs.push({
                    ...getDefaultConfig(),
                    stringGroupRef,
                    stringType: StringType.STRING_GROUP,
                });
            });
        }

        if (this.customValues?.length) {
            this.stringGroupRefOrCustomValuesConfigs.push({
                ...getDefaultConfig(),
                matchStrings: [...this.customValues],
                stringType: StringType.CUSTOM_VALUE,
            });
        }

        if (!this.stringGroupRefOrCustomValuesConfigs.length) {
            this.stringGroupRefOrCustomValuesConfigs.push(getDefaultConfig());
        }
    }
}
